import React, {
	useState,
	useCallback,
	type KeyboardEvent,
	type ElementRef,
	memo,
	type ChangeEvent,
	type SyntheticEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import akTextfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { TextCellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import { TextFieldWrapper } from '../../../common/ui/list-cell/styled.tsx';
import messages from './messages.tsx';
import type { NumberCellProps, NumberType } from './types.tsx';

const getInputValue = ({
	inputValue,
	fieldValue,
}: {
	inputValue?: string;
	fieldValue: NumberType | null;
}) => {
	if (typeof inputValue === 'undefined') {
		return fieldValue;
	}
	return typeof inputValue === 'string' && inputValue ? Number(inputValue) : null;
};

const NumberCell = memo<NumberCellProps>(
	({
		issueKey,
		fieldKey,
		fieldType = '',
		isEditable,
		fieldValue = null,
		fieldName,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		editMode: editModeNew = false,
		setEditMode: setEditModeNew = noop,
		cellRef,
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
	}: NumberCellProps) => {
		const [numValueOld, setNumValueOld] = useState<NumberType>(fieldValue);
		const [editMode, setEditMode] = fg('jira_list_keyboard_nav')
			? [editModeNew, setEditModeNew]
			: // eslint-disable-next-line react-hooks/rules-of-hooks
				useState<boolean>(false);

		const numValue = fg('jira_list_keyboard_nav')
			? getInputValue({ inputValue, fieldValue })
			: numValueOld;

		const setNumValueNew = (value: NumberType) =>
			onInputChange(value !== null ? String(value) : '');

		const setNumValue = fg('jira_list_keyboard_nav') ? setNumValueNew : setNumValueOld;

		const [{ value, error }, { saveValue, resetError }] = useEditField<NumberType, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: fieldValue,
		});
		const { createAnalyticsEvent } = useAnalyticsEvents();

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleClick in CellNavigation for generic behaviour or useImperativeHandle in this
		 * cell for custom behaviour
		 */
		const handleClick = useCallback(() => {
			if (isEditable && !editMode) {
				setEditMode(true);
				setNumValue(value);
			}
		}, [isEditable, editMode, setEditMode, setNumValue, value]);

		const saveAndClose = useCallback(
			(event: SyntheticEvent) => {
				if (!fg('jira_list_keyboard_nav')) {
					event.preventDefault();
				}
				if (editMode) {
					setEditMode(false);
					numValue !== value && saveValue(numValue, null, createAnalyticsEvent({}));
				}
			},
			[createAnalyticsEvent, editMode, numValue, saveValue, value, setEditMode],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Calling saveAndClose directly instead
		 */
		const handleSubmit = useCallback(
			(event: SyntheticEvent) => {
				saveAndClose(event);
				wrapperRef?.current?.focus();
			},
			[saveAndClose, wrapperRef],
		);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				resetError();
				const typedValue =
					event.target.value === '' ? null : parseFloat(Number(event.target.value).toFixed(3));
				setNumValue(typedValue);
			},
			[resetError, setNumValue],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Use handleKeyDown in CellNavigation for generic behaviour or handleKeyDownNew in this
		 * cell for custom behaviour
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				e.stopPropagation();
				!editMode && onKeyDown?.(e);
			},
			[editMode, onKeyDown],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated wrapperRef is now managed in CellNavigation
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated now managed in handleKeyDownNew
		 */
		const onTextFieldKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					setNumValue(value);
					wrapperRef?.current?.focus();
				}
			},
			[setNumValue, value, wrapperRef],
		);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Escape') {
					setNumValue(value || null);
				}
				return false;
			},
			[editMode, setNumValue, value],
		);

		const cellOverrides = useCallback(() => ({ onKeyDown: handleKeyDownNew }), [handleKeyDownNew]);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const numberCell = (
			<>
				{editMode ? (
					<form onSubmit={fg('jira_list_keyboard_nav') ? saveAndClose : handleSubmit}>
						<Textfield
							autoFocus
							type="number"
							onKeyDown={fg('jira_list_keyboard_nav') ? noop : onTextFieldKeyDown}
							onBlur={saveAndClose}
							value={numValue === null ? '' : numValue}
							onChange={handleChange}
							step="any"
						/>
					</form>
				) : (
					<NumberLabel>{value}</NumberLabel>
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<TextCellWrapper
					testId="business-list.ui.list-view.number-cell.text-field-wrapper"
					isEditable={isEditable}
					ariaLabel={fieldName}
				>
					{numberCell}
				</TextCellWrapper>
			);
		}

		return (
			<TextFieldWrapper
				isSelected={isSelected}
				onClick={handleClick}
				isEditable={isEditable}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				data-testid="business-list.ui.list-view.number-cell.text-field-wrapper"
				aria-label={fieldName}
			>
				{numberCell}
			</TextFieldWrapper>
		);
	},
);

export default NumberCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NumberLabel = styled.span({
	textAlign: 'right',
	paddingRight: token('space.100', '8px'),
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Textfield = styled(akTextfield)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> [data-ds--text-field--input]': {
		textAlign: 'right',
		paddingRight: token('space.100', '8px'),
	},
});
